/**
 * «Ρυθμίσεις συσκευής» του PDA (γρανάζι στην αρχική και στην οθόνη παραγγελίας): θέμα, πόσα
 * τραπέζια/κατηγορίες/προϊόντα ανά σειρά, μέγεθος καρτών, προϊόντα ως κάρτες ή λίστα, κάρτα
 * αναζήτησης (προεπιλογή όχι). Όλα ισχύουν
 * ΑΜΕΣΩΣ και μένουν σε αυτή τη συσκευή (devicePrefs + ThemeContext) — κανένα κουμπί «Αποθήκευση».
 */

import React from 'react';
import { RotateCcw } from 'lucide-react';
import { useTheme, Theme } from '../../../contexts/ThemeContext';
import { Sheet, Button } from './ui';
import { CardSize, WaiterPrefs, resetPrefs, setPrefs, useWaiterPrefs } from './devicePrefs';

/** Επιλογή σε γραμμή κουμπιών· ίδια όψη με τα υπόλοιπα «role-choice» του app (φωτεινό θέμα). */
function Segmented<V extends string | number | boolean>({ label, value, options, onChange }: {
  label: string;
  value: V;
  options: Array<[V, string]>;
  onChange: (value: V) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex gap-1.5">
      {options.map(([v, text]) => {
        const active = v === value;
        return (
          <button
            key={String(v)}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(v)}
            className={`role-choice flex-1 min-h-[44px] px-2 rounded-xl text-sm font-bold transition-colors ${
              active ? 'bg-blue-600 text-white' : 'bg-surface-1 text-t2 active:bg-surface-2'
            }`}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}

const Row: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <p className="text-sm font-semibold text-t2">
      {label}
      {hint && <span className="font-normal text-t4"> · {hint}</span>}
    </p>
    {children}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="px-3 pt-4 space-y-3" aria-label={title}>
    <h3 className="text-xs font-bold uppercase tracking-wider text-t3">{title}</h3>
    {children}
  </section>
);

const SIZES: Array<[CardSize, string]> = [['sm', 'Μικρές'], ['md', 'Μεσαίες'], ['lg', 'Μεγάλες']];

const DeviceSettingsSheet: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const prefs = useWaiterPrefs();
  const { theme, setTheme } = useTheme();
  const set = <K extends keyof WaiterPrefs>(key: K) => (value: WaiterPrefs[K]) => setPrefs({ [key]: value } as Partial<WaiterPrefs>);

  return (
    <Sheet
      title="Ρυθμίσεις συσκευής"
      subtitle="Ισχύουν αμέσως και μόνο σε αυτό το κινητό"
      onClose={onClose}
      footer={
        <div className="pb-1">
          <Button tone="neutral" full onClick={() => { resetPrefs(); setTheme('dark'); }}>
            <RotateCcw className="w-5 h-5" />
            Επαναφορά προεπιλογών
          </Button>
        </div>
      }
    >
      <div className="pb-4">
        <Section title="Εμφάνιση">
          <Row label="Θέμα">
            <Segmented<Theme>
              label="Θέμα"
              value={theme}
              onChange={setTheme}
              options={[['dark', 'Σκούρο'], ['light', 'Φωτεινό'], ['system', 'Αυτόματο']]}
            />
          </Row>
        </Section>

        <Section title="Τραπέζια">
          <Row label="Ανά σειρά" hint="στην αρχική">
            <Segmented label="Τραπέζια ανά σειρά" value={prefs.tableColumns} onChange={set('tableColumns')}
              options={[[2, '2'], [3, '3'], [4, '4']]} />
          </Row>
        </Section>

        <Section title="Κατηγορίες">
          <Row label="Ανά σειρά">
            <Segmented label="Κατηγορίες ανά σειρά" value={prefs.categoryColumns} onChange={set('categoryColumns')}
              options={[[3, '3'], [4, '4'], [5, '5']]} />
          </Row>
          <Row label="Μέγεθος καρτών">
            <Segmented label="Μέγεθος καρτών κατηγοριών" value={prefs.categorySize} onChange={set('categorySize')} options={SIZES} />
          </Row>
          <Row label="Αναζήτηση προϊόντος" hint="κάρτα πρώτη στον πίνακα">
            <Segmented label="Αναζήτηση προϊόντος" value={prefs.showSearch} onChange={set('showSearch')}
              options={[[false, 'Όχι'], [true, 'Ναι']]} />
          </Row>
        </Section>

        <Section title="Προϊόντα">
          <Row label="Εμφάνιση">
            <Segmented label="Εμφάνιση προϊόντων" value={prefs.productView} onChange={set('productView')}
              options={[['cards', 'Κάρτες'], ['list', 'Λίστα']]} />
          </Row>
          {prefs.productView === 'cards' && (
            <Row label="Ανά σειρά" hint="με 3 τα μεγάλα ονόματα κόβονται">
              <Segmented label="Προϊόντα ανά σειρά" value={prefs.productColumns} onChange={set('productColumns')}
                options={[[1, '1'], [2, '2'], [3, '3']]} />
            </Row>
          )}
          <Row label={prefs.productView === 'cards' ? 'Μέγεθος καρτών' : 'Μέγεθος γραμμών'}>
            <Segmented label="Μέγεθος προϊόντων" value={prefs.productSize} onChange={set('productSize')} options={SIZES} />
          </Row>
        </Section>
      </div>
    </Sheet>
  );
};

export default DeviceSettingsSheet;
